import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useClient } from '@utils/api-client';
import { messageQueryKeys } from './messageQueryKeys';
import { MessagesSearchResponse, MessageSearchRequestQuery } from '@lio/api-types';

// Anchor based infinite scroll strategy: we page "older" messages by passing anchorId=<currentOldest>&beforeLimit=<n>.
// For searches, the same anchor pattern applies while maintaining independent cache keys per searchText value.
export const useSearchMessages = ({
  policyId,
  quoteId,
  params,
  enabled,
}: {
  policyId?: string;
  quoteId?: string;
  params: Omit<MessageSearchRequestQuery, 'quoteId' | 'policyId'>;
  enabled: boolean;
}) => {
  const { searchText, beforeLimit, afterLimit, anchorId, pageLimit } = params;
  const client = useClient();

  // Build a stable base key so each searchText maintains its own cache.
  const queryKey = messageQueryKeys.searchWithParams({
    policyId,
    quoteId,
    ...params,
  } as any);

  type PageParam = { anchorId?: string; direction: 'older' | 'newer' } | number | undefined;
  const result = useInfiniteQuery<MessagesSearchResponse, Error, MessagesSearchResponse, any, PageParam>({
    queryKey,
    enabled,
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();

      if (quoteId) {
        searchParams.append('quoteId', quoteId);
      }

      if (policyId) {
        searchParams.append('policyId', policyId);
      }

      if (searchText && searchText !== '') {
        searchParams.append('searchText', searchText);
      }

      if (pageParam && typeof pageParam === 'object' && pageParam?.anchorId) {
        searchParams.append('anchorId', pageParam.anchorId);
        if (pageParam.direction === 'older') {
          searchParams.append('beforeLimit', String(beforeLimit));
        } else if (pageParam.direction === 'newer') {
          searchParams.append('afterLimit', String(afterLimit));
        }
      } else {
        if (anchorId) {
          searchParams.append('anchorId', anchorId);

          if (afterLimit) {
            searchParams.append('afterLimit', String(afterLimit));
          }
        }
        if (beforeLimit) {
          searchParams.append('beforeLimit', String(beforeLimit ?? 20));
        }
      }

      if (pageLimit) {
        searchParams.append('pageLimit', String(pageLimit));
        if (typeof pageParam === 'number') {
          searchParams.append('pageOffset', String(pageParam));
        }
      }

      const qs = searchParams.toString();

      return client.get(`messages?${qs}`).json<MessagesSearchResponse>();
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.anchor?.oldestItemIncludedId) {
        return { anchorId: firstPage.anchor.oldestItemIncludedId, direction: 'older' } as PageParam;
      }
      return undefined;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.pagination?.total) {
        if (!lastPage?.pagination) return undefined;
        if (!lastPage.pagination.pageLimit) return undefined;
        if (!lastPage.pagination.total) return undefined;
        const { total, pageLimit } = lastPage.pagination;
        const fetchedPageCount = allPages.length;
        const nextPageOffsetValue = fetchedPageCount * pageLimit;
        if (nextPageOffsetValue > total) return undefined;

        return nextPageOffsetValue;
      }

      if (lastPage.anchor?.newestItemIncludedId) {
        return { anchorId: lastPage.anchor.newestItemIncludedId, direction: 'newer' } as PageParam;
      }

      return undefined;
    },
    refetchOnWindowFocus: false,
    staleTime: 20000,
    initialPageParam: undefined as any,
  });

  // Flatten messages then deduplicate by id (API pages can overlap around anchors)
  const messages = useMemo(() => {
    const flat = (result.data as InfiniteData<MessagesSearchResponse> | undefined)?.pages.flatMap((p) => p.items) || [];
    if (flat.length <= 1) return flat;
    const seen = new Set<string>();
    const deduped = [] as typeof flat;
    for (const m of flat) {
      if (!m?.id) continue; // skip invalid
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      deduped.push(m);
    }
    return deduped;
  }, [result.data]);

  return {
    ...result,
    firstPage:
      result.data && 'pages' in result.data && Array.isArray(result.data.pages)
        ? result.data.pages[0]
        : (result.data as MessagesSearchResponse | undefined),
    lastPage:
      result.data && 'pages' in result.data && Array.isArray(result.data.pages)
        ? result.data.pages[result.data.pages.length - 1]
        : (result.data as MessagesSearchResponse | undefined),
    pagination:
      result.data && 'pages' in result.data && Array.isArray(result.data.pages)
        ? result.data.pages[result.data.pages.length - 1]?.pagination
        : (result.data as MessagesSearchResponse | undefined)?.pagination,
    messages,
    loadOlder: result.fetchPreviousPage,
    loadNewer: result.fetchNextPage,
    isLoadingOlder: result.isFetchingPreviousPage,
    isLoadingNewer: result.isFetchingNextPage,
  };
};

export type UseSearchMessagesReturn = ReturnType<typeof useSearchMessages>;
